import { useFlags } from 'launchdarkly-react-client-sdk'
import { Fragment } from 'react'
import { Helmet } from 'react-helmet'
import PropTypes from 'prop-types'

import DashboardHeader from './header/DashboardHeader'
import ExperimentationLinks from './ExperimentationLinks'
import UniqueTargetsAttackedCard from './cards/UniqueTargetsAttackedCard'
import ScheduledExperimentsCard from './cards/ScheduledExperimentsCard'
import RecentExperimentsCard from './cards/RecentExperimentsCard'
import TargetsCard from './cards/TargetsCard'
import TotalAttacksRunCard from './cards/TotalAttacksRunCard'
import HeaderSlider from './header/HeaderSlider'
import RecentScenariosCard from './cards/RecentScenariosCard'
import TechReadinessBanner from './notification-banners/TechReadinessBanner'

import ServicesCard from 'components/dashboard/cards/ServicesCard'
import TutorialBanner from 'components/dashboard/footer/TutorialBanner'
import DashboardUpgradeBanner from 'components/dashboard/footer/DashboardUpgradeBanner'
import Header, { Heading } from 'components/navigation/Header'
import Content from 'components/common/Content'
import theme from 'theme'
import CardList from 'components/common/CardList'
import useAttackResource from 'hooks/use-attack-resource'
import useClientResource from 'hooks/use-client-resource'
import useAuth from 'hooks/use-auth'

export const CARD_WIDTH = 500

const Placeholder = ({ children }) => (
  <div
    css={{
      width: '100%',
      height: 100,
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      backgroundColor: theme.colors.gray[0],
      marginTop: theme.space[1]
    }}
  >
    {children}
  </div>
)

Placeholder.propTypes = {
  children: PropTypes.node
}

export default function Dashboard() {
  const { isFreeEdition } = useAuth()
  const { serviceDiscovery: isServicesEnabled } = useFlags()

  // Used in 2 cards

  const {
    activeScenarios,
    activeScenariosLoading,
    completed: completedAttacks,
    active: activeAttacks,
    partialCompletedAttacksLoaded,
    kubernetesAttacksLoading,
    applicationAttacksLoading
  } = useAttackResource({ fetchCompletedFully: false })

  // using partial fetch only as we don't need the full fetch anymore
  const attacksLoading =
    !partialCompletedAttacksLoaded ||
    kubernetesAttacksLoading ||
    applicationAttacksLoading

  const allAttacks = [].concat(activeAttacks, completedAttacks)
  const clientResource = useClientResource()

  return (
    <Fragment>
      <Helmet title="Dashboard">
        {/* Hotjar tracking code */}
        {isFreeEdition && (
          <script>{`
            (function(h,o,t,j,a,r){
              h.hj=h.hj||function(){(h.hj.q=h.hj.q||[]).push(arguments)};
              h._hjSettings={hjid:1726087,hjsv:6};
              a=o.getElementsByTagName('head')[0];
              r=o.createElement('script');r.async=1;
              r.src=t+h._hjSettings.hjid+j+h._hjSettings.hjsv;
              a.appendChild(r);
            })(window,document,'https://static.hotjar.com/c/hotjar-','.js?sv=');
          `}</script>
        )}
      </Helmet>
      <Header>
        <Heading>Dashboard</Heading>
      </Header>
      <Content data-test-dashboard id="Dashboard">
        <DashboardHeader />
        <TechReadinessBanner
          css={{
            marginTop: theme.space[0]
          }}
        />

        <ExperimentationLinks
          hasClients={clientResource.hasClients}
          isFreeEdition={isFreeEdition}
        />
        <CardList
          css={{
            marginTop: theme.space[4]
          }}
          minWidth={CARD_WIDTH}
        >
          {isServicesEnabled && <ServicesCard />}
          <TargetsCard
            allAttacks={allAttacks}
            attacksLoading={attacksLoading}
            clientResource={clientResource}
          />
          <TotalAttacksRunCard />
          <RecentScenariosCard
            scenarios={activeScenarios}
            scenariosLoading={activeScenariosLoading}
          />
          <RecentExperimentsCard
            allAttacks={allAttacks}
            attacksLoading={attacksLoading}
          />
          <ScheduledExperimentsCard />
          {!isFreeEdition && <UniqueTargetsAttackedCard />}
        </CardList>
        {!isFreeEdition && <TutorialBanner />}
        {isFreeEdition && (
          <div css={{ marginTop: theme.space[3] }}>
            <HeaderSlider />
          </div>
        )}
        {isFreeEdition && <DashboardUpgradeBanner />}
      </Content>
    </Fragment>
  )
}
